import cx from "classnames";
import { Flex, Text } from "@theorgchartdev/storybook";
import type { ReactNode } from "react";

type AgentStatTileProps = {
  value: string;
  caption: string;
  /** Small suffix rendered beside the value (e.g. "hrs", "/month"). */
  unit?: string;
  /** Small right-aligned share rendered beside the value (action breakdown). */
  percent?: string;
  /** Action-breakdown layout: caption above the value instead of below. */
  captionFirst?: boolean;
  dataTestId?: string;
};

export const AgentStatTile = ({
  value,
  caption,
  unit,
  percent,
  captionFirst = false,
  dataTestId
}: AgentStatTileProps) => {
  const valueRow = (
    <Flex
      direction="row"
      gap="4px"
      alignItems="baseline"
      justifyContent={captionFirst ? "space-between" : "center"}
      className="ocn-agent-stat-tile-value-row"
    >
      <Text size="small" className="oc-font-weight-500" dataTestId={dataTestId}>
        {value}
        {unit ? (
          <Text size="xsmall" colorModifier="grey" element="span">
            {" "}
            {unit}
          </Text>
        ) : null}
      </Text>
      {percent ? (
        <Text size="xsmall" colorModifier="grey">
          {percent}
        </Text>
      ) : null}
    </Flex>
  );

  const captionRow = (
    <Text
      size="xsmall"
      colorModifier="grey"
      className={cx("ocn-agent-stat-tile-caption", {
        "ocn-agent-stat-tile-caption--top": captionFirst
      })}
    >
      {caption}
    </Text>
  );

  return (
    <Flex direction="column" gap="4px" className="ocn-agent-stat-tile">
      {captionFirst ? (
        <>
          {captionRow}
          {valueRow}
        </>
      ) : (
        <>
          {valueRow}
          {captionRow}
        </>
      )}
    </Flex>
  );
};

type AgentLabelValueRowProps = {
  label: string;
  value?: string;
  /** Custom right-hand content (e.g. a link) instead of a plain value. */
  valueContent?: ReactNode;
  dataTestId?: string;
};

export const AgentLabelValueRow = ({
  label,
  value,
  valueContent,
  dataTestId
}: AgentLabelValueRowProps) => {
  return (
    <Flex
      direction="row"
      gap="16px"
      alignItems="baseline"
      justifyContent="space-between"
      className="ocn-agent-label-value-row"
    >
      <Text size="xsmall" colorModifier="grey" className="ocn-agent-label-value-row-label">
        {label}
      </Text>
      {valueContent ?? (
        <Text size="xsmall" className="ocn-agent-label-value-row-value" dataTestId={dataTestId}>
          {value}
        </Text>
      )}
    </Flex>
  );
};
